/* SSi website – Sample lesson + pricing.
 *
 * The sample frame is the real <ssi-lesson-player> custom element shipped
 * from /lesson-player/ — Shadow DOM, no framework deps. The player JS is
 * loaded once in index.html and self-registers as a custom element, so
 * <ssi-lesson-player> works as a regular JSX tag here.
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ensureLessonPlayer } from '../lessonPlayerLoader.js';
import { useOffer } from '../offers/OfferContext.jsx';

export function SampleAndPricing({ t, defaultLang, defaultBilling = 'annual' }) {
  // Offer test (Design handoff 2026-06-11): when a variant is active, the
  // subscription aside pitches that variant instead of the 7-day trial.
  const offer = useOffer();
  const aside = offer ? offer.config.aside : null;
  const LANGS = [
    { code:'en-es',   name:'Spanish',    flag:'spanish',    greet:'Hola' },
    { code:'en-fr',   name:'French',     flag:'french',     greet:'Bonjour' },
    { code:'en-de',   name:'German',     flag:'german',     greet:'Hallo' },
    { code:'en-it',   name:'Italian',    flag:'italian',    greet:'Ciao' },
    { code:'en-pt',   name:'Portuguese', flag:'portuguese', greet:'Olá' },
    { code:'en-cmn',   name:'Mandarin',   flag:'chinese',    greet:'你好' },
    { code:'en-ja',   name:'Japanese',   flag:'japanese',   greet:'こんにちは' },
    { code:'en-ko',   name:'Korean',     flag:'korean',     greet:'안녕' },
    { code:'en-ar',   name:'Arabic',     flag:'arabic',     greet:'مرحبا' },
    {
      code:'en-cy-south', name:'Welsh',      flag:'wales',      greet:'Shwmae',
      dialects: [
        { code:'en-cy-south', label:'Welsh (South)' },
        { code:'en-cy-north', label:'Welsh (North)' },
      ],
    },
  ];

  const initialIdx = useMemo(() => {
    if (!defaultLang) return 0;
    const idx = LANGS.findIndex(L => L.code === defaultLang);
    return idx >= 0 ? idx : 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLang]);
  const [i, setI] = useState(initialIdx);
  const [billing, setBilling] = useState(aside ? aside.defaultBilling : defaultBilling);
  const [duration, setDuration] = useState(3);
  const [dialectIdx, setDialectIdx] = useState(0);
  const [dialectOpen, setDialectOpen] = useState(false);
  const dialectMenuRef = useRef(null);
  const playerRef = useRef(null);

  const lang = LANGS[i];
  const dialects = lang.dialects || null;
  const activeCourse = dialects ? dialects[dialectIdx].code : lang.code;
  const activeName   = dialects ? dialects[dialectIdx].label : lang.name;

  // Kick the lesson-player script load on mount. Idempotent — multiple
  // mounts (route revisits, StrictMode double-effect) reuse the cached
  // promise. The custom element registers globally on first script load,
  // so subsequent <ssi-lesson-player> renders work immediately.
  useEffect(() => {
    ensureLessonPlayer();
  }, []);

  useEffect(() => { setDialectOpen(false); }, [i]);

  useEffect(() => {
    if (!dialectOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDialectOpen(false); };
    const onClick = (e) => {
      if (dialectMenuRef.current && !dialectMenuRef.current.contains(e.target)) {
        setDialectOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [dialectOpen]);

  // Forward the selected course to the custom element. The player's
  // attributeChangedCallback handles the manifest reload — we just nudge
  // playback to a stop first so a course switch never has stale audio
  // continuing on the new chip.
  useEffect(() => {
    const el = playerRef.current;
    if (!el || lang.disabled) return;
    if (typeof el._pause === 'function' && el._playing) el._pause();
    el.setAttribute('course', activeCourse);
  }, [activeCourse, lang.disabled]);

  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;
    el.setAttribute('duration', String(duration));
  }, [duration]);

  const selectLang = (idx) => {
    if (LANGS[idx].disabled) return;
    setI(idx);
  };

  const FLAG_ASSET_NAMES = ['spanish','french','german','italian','portuguese','chinese','japanese','wales','ireland','korean','arabic'];

  // The Pay route (Stage 3) reads landing+channel from useAttribution()
  // and forwards them to Stripe metadata. Pay link only carries the
  // course + plan; attribution context is consumed inside /pay itself,
  // not stamped onto outbound URLs (replaces the static-site
  // attribution.js MutationObserver pattern).
  // Variants without a billing toggle have no plan choice to forward.
  const payHref = (aside && !aside.toggle)
    ? `/pay?course=${activeCourse}`
    : `/pay?course=${activeCourse}&plan=${billing}`;

  return (
    <section id="sample" className="ssi-sample" style={sampleStyles.wrap}>
      <div style={sampleStyles.head}>
        <div style={sampleStyles.kicker}>{t.sampleKicker}</div>
        <h2 style={sampleStyles.h2}>{t.sampleTitle}</h2>
        <p style={sampleStyles.lede}>{t.sampleLede}</p>
      </div>

      <div style={sampleStyles.pickerWrap}>
        <div style={sampleStyles.pickerLabel}>Choose your language</div>
        <div style={sampleStyles.picker}>
          {LANGS.map((L, idx) => {
            const active = idx === i;
            const flagExt = L.flag === 'wales' ? 'png' : 'svg';
            const flagAsset = FLAG_ASSET_NAMES.includes(L.flag)
              ? `/assets/flag-${L.flag}.${flagExt}?v=4` : null;
            const chipDialects = L.dialects || null;
            const showDialectMenu = !!(chipDialects && dialectOpen);
            const onChipClick = () => {
              if (L.disabled) return;
              if (chipDialects) {
                setDialectOpen(o => !o);
              } else {
                selectLang(idx);
                setDialectOpen(false);
              }
            };
            return (
              <span
                key={L.code}
                ref={chipDialects ? dialectMenuRef : null}
                style={sampleStyles.chipWrap}
              >
                <button
                  type="button"
                  onClick={onChipClick}
                  disabled={L.disabled}
                  aria-haspopup={chipDialects ? 'listbox' : undefined}
                  aria-expanded={chipDialects ? showDialectMenu : undefined}
                  style={{
                    ...sampleStyles.chip,
                    opacity: L.disabled ? 0.4 : 1,
                    cursor: L.disabled ? 'not-allowed' : 'pointer',
                    transform: active ? 'translateY(-2px)' : 'none',
                  }}>
                  <span style={sampleStyles.chipFlagSlot}>
                    <span style={{
                      ...sampleStyles.chipFlag,
                      boxShadow: active
                        ? '0 0 0 3px var(--brandRed), 0 6px 16px rgba(178,25,45,.28)'
                        : '0 0 0 1px rgba(30,20,18,.08), 0 1px 2px rgba(30,20,18,.06)',
                    }}>
                      {flagAsset
                        ? <img src={flagAsset} alt="" style={sampleStyles.chipFlagImg}/>
                        : <span style={sampleStyles.chipGlyph}>{L.greet}</span>}
                    </span>
                    {chipDialects && showDialectMenu && (
                      <span style={{
                        ...sampleStyles.chipChevron,
                        background: active ? 'var(--brandRed)' : '#fff',
                        color: active ? '#fff' : 'var(--fg-1)',
                        boxShadow: active
                          ? '0 2px 6px rgba(178,25,45,.35)'
                          : '0 0 0 1px rgba(30,20,18,.10), 0 1px 2px rgba(30,20,18,.08)',
                      }}>
                        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{
                          transform: showDialectMenu ? 'rotate(180deg)' : 'none',
                          transition: 'transform 160ms ease',
                        }}>
                          <path d="M1.7 3.6L5 6.9l3.3-3.3" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    )}
                  </span>
                  <span style={{
                    ...sampleStyles.chipName,
                    color: active ? 'var(--brandRed)' : 'var(--fg-1)',
                    fontWeight: active ? 600 : 500,
                  }}>
                    {L.name}
                  </span>
                  {L.disabled && (
                    <span style={sampleStyles.chipSoon}>Soon</span>
                  )}
                </button>
                {showDialectMenu && (
                  <ul role="listbox" aria-label="Welsh dialect" style={sampleStyles.dialectMenu}>
                    {chipDialects.map((d, dIdx) => {
                      const sel = dIdx === dialectIdx;
                      return (
                        <li key={d.code} role="option" aria-selected={sel}>
                          <button
                            type="button"
                            onClick={() => { setI(idx); setDialectIdx(dIdx); setDialectOpen(false); }}
                            style={{
                              ...sampleStyles.dialectItem,
                              fontWeight: sel ? 600 : 500,
                              color: sel ? 'var(--brandRed)' : 'var(--fg-1)',
                            }}
                          >
                            <span>{d.label}</span>
                            {sel && (
                              <svg width="13" height="13" viewBox="0 0 12 12" aria-hidden="true" style={{marginLeft: 10, flex: '0 0 auto'}}>
                                <path d="M2.5 6.5L5 9l4.5-5.5" stroke="currentColor" strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </span>
            );
          })}
        </div>
      </div>

      <div className="ssi-sample__card" style={sampleStyles.card}>
        <div className="ssi-sample__playerSide" style={sampleStyles.playerSide}>
          {dialects && (
            <div style={sampleStyles.dialectTabs} role="tablist" aria-label="Welsh dialect">
              {dialects.map((d, dIdx) => {
                const sel = dIdx === dialectIdx;
                return (
                  <button
                    key={d.code}
                    role="tab"
                    type="button"
                    aria-selected={sel}
                    onClick={() => setDialectIdx(dIdx)}
                    style={{
                      ...sampleStyles.dialectTab,
                      background: sel ? '#fff' : 'transparent',
                      color: sel ? 'var(--brandRed)' : 'var(--grey)',
                      fontWeight: sel ? 600 : 500,
                      boxShadow: sel ? '0 1px 2px rgba(30,20,18,.08), 0 4px 10px rgba(30,20,18,.06)' : 'none',
                    }}>
                    {d.label}
                  </button>
                );
              })}
            </div>
          )}
          <div style={sampleStyles.playerWrap}>
            <ssi-lesson-player
              ref={playerRef}
              course={activeCourse}
              duration={String(duration)}
              autoplay="false"
            ></ssi-lesson-player>
          </div>
          <div style={sampleStyles.playerCaption}>
            Real lesson audio · {activeName}
          </div>

          <div style={sampleStyles.durToggle} role="tablist" aria-label="Demo length">
            {[
              { val: 3,  label: '3 min', sub: 'Taster' },
              { val: 30, label: '30 min', sub: 'Full session' },
            ].map(o => {
              const active = duration === o.val;
              return (
                <button
                  key={o.val}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => setDuration(o.val)}
                  style={{
                    ...sampleStyles.durBtn,
                    background: active ? '#fff' : 'transparent',
                    color: active ? 'var(--fg-1)' : 'var(--grey)',
                    boxShadow: active ? '0 1px 2px rgba(30,20,18,.08), 0 4px 10px rgba(30,20,18,.06)' : 'none',
                  }}>
                  <span style={{fontWeight: active ? 600 : 500}}>{o.label}</span>
                  <span style={sampleStyles.durSub}>{o.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="ssi-sample__offer" style={sampleStyles.offer}>
          <div style={sampleStyles.offerInner}>
            <div style={sampleStyles.offerKicker}>Start speaking {lang.name}</div>

            {(!aside || aside.toggle) && (
              <div style={sampleStyles.billingToggle} role="tablist" aria-label="Billing period">
                {['monthly','annual'].map(p => {
                  const active = billing === p;
                  return (
                    <button
                      key={p}
                      role="tab"
                      type="button"
                      aria-selected={active}
                      onClick={() => setBilling(p)}
                      style={{
                        ...sampleStyles.billingBtn,
                        background: active ? '#fff' : 'transparent',
                        color: active ? 'var(--fg-1)' : 'var(--grey)',
                        boxShadow: active ? '0 1px 2px rgba(30,20,18,.08), 0 4px 10px rgba(30,20,18,.06)' : 'none',
                        fontWeight: active ? 600 : 500,
                      }}>
                      {p === 'monthly' ? 'Monthly' : 'Annual'}
                      {p === 'annual' && <span style={sampleStyles.savingPill}>{aside ? aside.savePill : 'Save £30'}</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {aside ? (() => {
              // Per-billing price when the toggle shows; fixed price otherwise.
              const price = aside.toggle ? aside[billing] : aside.static;
              return (
                <>
                  <div style={sampleStyles.price}>
                    {price.curr ? <span style={sampleStyles.priceCurr}>{price.curr}</span> : null}
                    <span style={sampleStyles.priceNum}>{price.num}</span>
                    <span style={sampleStyles.pricePer}>{price.per}</span>
                  </div>
                  {price.note && <div style={sampleStyles.priceNote}>{price.note}</div>}

                  <div style={sampleStyles.trialRow}>
                    <span style={sampleStyles.trialDot} aria-hidden/>
                    <span style={sampleStyles.trialText}>
                      <strong style={{color:'var(--fg-1)'}}>{aside.trialStrong}</strong> {aside.trialRest}
                    </span>
                  </div>

                  <ul style={sampleStyles.perks}>
                    {aside.perks.map(p => <li key={p} style={sampleStyles.perk}>{p}</li>)}
                    {aside.toggle && (
                      <li style={sampleStyles.perk}>
                        {billing === 'annual' ? aside.perkAnnual : aside.perkMonthly}
                      </li>
                    )}
                  </ul>
                  <Link to={payHref} style={sampleStyles.cta}>
                    {aside.cta}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{marginLeft:8}}>
                      <path d="M2 7h10m0 0L8 3m4 4L8 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                  <div style={sampleStyles.finePrint}>{aside.fine}</div>
                </>
              );
            })() : (
              <>
                <div style={sampleStyles.price}>
                  <span style={sampleStyles.priceCurr}>£</span>
                  <span style={sampleStyles.priceNum}>{billing === 'annual' ? '150' : '15'}</span>
                  <span style={sampleStyles.pricePer}>{billing === 'annual' ? '/year' : '/month'}</span>
                </div>

                <div style={sampleStyles.trialRow}>
                  <span style={sampleStyles.trialDot} aria-hidden/>
                  <span style={sampleStyles.trialText}>
                    <strong style={{color:'var(--fg-1)'}}>7 days free</strong> to make sure you&rsquo;re loving it. No card charged until day 8.
                  </span>
                </div>

                <ul style={sampleStyles.perks}>
                  <li style={sampleStyles.perk}>All 11 courses included</li>
                  <li style={sampleStyles.perk}>Cancel any time, no contract</li>
                  <li style={sampleStyles.perk}>
                    {billing === 'annual' ? 'Two months free vs monthly' : 'Switch to annual whenever'}
                  </li>
                </ul>
                <Link to={payHref} style={sampleStyles.cta}>
                  Start your 7 days free
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{marginLeft:8}}>
                    <path d="M2 7h10m0 0L8 3m4 4L8 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
                <div style={sampleStyles.finePrint}>
                  Cancel in the app before day 7 and you won&rsquo;t be charged. No guilt-tripping if you miss a day.
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

const sampleStyles = {
  wrap: { background: 'var(--offWhite, #FDF5F5)', padding: '88px 32px 96px' },
  head: { maxWidth: 720, margin: '0 auto 44px', textAlign: 'center' },
  kicker: { fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 600, marginBottom: 14 },
  h2: { fontFamily: 'var(--font-display)', fontSize: 52, lineHeight: 1.05, margin: '0 0 18px', letterSpacing: '-.01em' },
  lede: { fontSize: 17, color: 'var(--grey)', lineHeight: 1.55, margin: 0 },

  card: {
    maxWidth: 1120, margin: '0 auto',
    display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'stretch',
    background: '#fff', borderRadius: 24, overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 18px 48px -20px rgba(73,3,0,.18)',
    border: '1px solid rgba(30,20,18,.06)',
  },

  playerSide: {
    padding: '40px 20px 28px',
    background: 'linear-gradient(180deg, #fff 0%, #faf7f2 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18,
    borderRight: '1px solid rgba(30,20,18,.08)',
  },
  playerWrap: { width: '100%', maxWidth: 380, display: 'flex', justifyContent: 'center' },
  playerCaption: { fontSize: 12.5, color: 'var(--grey)', letterSpacing: '.02em', fontVariantNumeric: 'tabular-nums' },

  dialectMenu: {
    position: 'absolute', top: 'calc(100% + 8px)', left: '50%',
    transform: 'translateX(-50%)', margin: 0, padding: 4, listStyle: 'none',
    background: '#fff', border: '1px solid rgba(30,20,18,.10)', borderRadius: 12,
    boxShadow: '0 12px 32px rgba(30,20,18,.14), 0 2px 6px rgba(30,20,18,.06)',
    minWidth: 168, zIndex: 20, textAlign: 'left',
  },
  dialectItem: {
    appearance: 'none', background: 'transparent', border: 0, width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    font: 'inherit', fontSize: 13, padding: '8px 10px', borderRadius: 8,
    cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
  },

  dialectTabs: { display: 'inline-flex', padding: 4, background: 'rgba(30,20,18,.05)', borderRadius: 999, gap: 2 },
  dialectTab: {
    border: 0, padding: '7px 16px', borderRadius: 999, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 12.5, letterSpacing: '.01em',
    background: 'transparent',
    transition: 'background 160ms ease, color 160ms ease, box-shadow 160ms ease',
    whiteSpace: 'nowrap',
  },

  durToggle: { display: 'inline-flex', padding: 4, background: 'rgba(30,20,18,.05)', borderRadius: 999, gap: 2, marginTop: 4 },
  durBtn: {
    border: 0, padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, letterSpacing: '-.005em',
    transition: 'background 160ms ease, color 160ms ease, box-shadow 160ms ease',
    display: 'inline-flex', alignItems: 'baseline', gap: 6,
  },
  durSub: { fontSize: 11, color: 'var(--grey)', fontWeight: 500, letterSpacing: 0 },

  offer: { padding: '44px 44px 40px', background: '#fff', display: 'flex', alignItems: 'center' },
  offerInner: { width: '100%' },
  offerKicker: { fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--brandRed)', fontWeight: 600, marginBottom: 14 },
  billingToggle: { display: 'inline-flex', padding: 4, background: 'rgba(30,20,18,.05)', borderRadius: 999, marginBottom: 20, gap: 2 },
  billingBtn: {
    position: 'relative', border: 0, padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
    fontSize: 13, fontFamily: 'inherit', letterSpacing: '-.005em',
    transition: 'background 160ms ease, color 160ms ease, box-shadow 160ms ease',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  savingPill: {
    fontSize: 10, fontWeight: 600, letterSpacing: '.04em',
    background: 'var(--brandRed)', color: '#fff',
    padding: '2px 7px', borderRadius: 999, textTransform: 'uppercase',
  },
  trialRow: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '12px 14px', background: 'rgba(178,25,45,.06)',
    border: '1px solid rgba(178,25,45,.16)', borderRadius: 10, marginBottom: 22,
  },
  trialDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--brandRed)', flexShrink: 0, marginTop: 6 },
  trialText: { fontSize: 13.5, lineHeight: 1.5, color: 'var(--grey)' },
  price: { display: 'flex', alignItems: 'baseline', gap: 2, color: 'var(--fg-1)', marginBottom: 22 },
  priceCurr: { fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 500, color: 'var(--brandRed)' },
  priceNum: { fontFamily: 'var(--font-display)', fontSize: 72, lineHeight: 1, letterSpacing: '-.02em', fontWeight: 500 },
  pricePer: { fontSize: 16, color: 'var(--grey)', marginLeft: 4 },
  priceNote: { fontSize: 12.5, color: 'var(--grey)', margin: '-12px 0 18px', lineHeight: 1.4 },
  perks: { listStyle: 'none', margin: '0 0 28px', padding: 0, borderTop: '1px solid rgba(30,20,18,.08)' },
  perk: {
    padding: '12px 0', borderBottom: '1px solid rgba(30,20,18,.08)',
    fontSize: 14.5, color: 'var(--fg-1)', lineHeight: 1.4,
    display: 'flex', alignItems: 'center',
  },
  cta: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--brandRed)', color: '#fff',
    padding: '16px 22px', borderRadius: 12,
    fontSize: 15.5, fontWeight: 600, textDecoration: 'none',
    boxShadow: '0 6px 16px -6px rgba(178,25,45,.55)',
    width: '100%', boxSizing: 'border-box',
  },
  finePrint: { fontSize: 12.5, color: 'var(--grey)', marginTop: 14, lineHeight: 1.45, textAlign: 'center' },

  pickerWrap: { maxWidth: 1120, margin: '0 auto 40px', textAlign: 'center' },
  pickerLabel: { fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--grey)', fontWeight: 600, marginBottom: 20 },
  picker: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '22px 26px' },
  chipWrap: { position: 'relative', display: 'inline-flex' },
  chipChevron: {
    position: 'absolute', bottom: 0, right: 0,
    width: 22, height: 22, borderRadius: '50%',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 160ms ease, color 160ms ease, box-shadow 160ms ease',
  },
  chipFlagSlot: { position: 'relative', width: 60, height: 60, display: 'inline-block' },
  chip: {
    background: 'transparent', border: 'none', padding: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    fontFamily: 'inherit', transition: 'transform 160ms ease',
    minWidth: 72, position: 'relative',
  },
  chipFlag: {
    width: 60, height: 60, borderRadius: '50%', overflow: 'hidden',
    background: 'var(--brandRed)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'box-shadow 160ms ease',
  },
  chipFlagImg: { width: '100%', height: '100%', objectFit: 'cover' },
  chipGlyph: {
    color: '#fff', fontFamily: 'var(--font-display)',
    fontSize: 15, lineHeight: 1, fontWeight: 500, textAlign: 'center',
    padding: '0 4px', letterSpacing: '-.01em',
  },
  chipName: { fontSize: 13, transition: 'color 160ms ease' },
  chipSoon: {
    position: 'absolute', top: -6, right: -8,
    fontSize: 9, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
    background: 'var(--grey)', color: '#fff',
    padding: '2px 6px', borderRadius: 6,
  },
};
